import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  Users,
  AlertCircle,
  Search,
  Sparkles,
  Lock,
} from "lucide-react";

import { workshopsService } from "@/api/workshopsService";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Reveal } from "@/components/site/SiteLayout";

import WorkshopRegistrationForm from "@/components/workshops/WorkshopRegistrationForm";
import WorkshopRegistrationSuccess from "@/components/workshops/WorkshopRegistrationSuccess";

/**
 * ============================================================
 * WORKSHOP REGISTRATION PAGE (/workshops/:slug/register)
 * ============================================================
 * Handles student registration for an open workshop cohort:
 * - Dynamic data fetching via workshopsService
 * - Lifecycle state checks (Draft/404, Completed, Cancelled, Closed)
 * - Two-column responsive design (Workshop Logistics + 5-Field Form)
 * - Transitions to confirmation screen upon verified submission
 */
export default function WorkshopRegister() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredStudentName, setRegisteredStudentName] = useState("");

  const defaultTitleRef = useRef(document.title);
  const defaultDescRef = useRef(
    document.querySelector('meta[name="description"]')?.getAttribute("content") || ""
  );

  const loadWorkshop = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    // Call service with allowDraft = false (strictly public visibility)
    workshopsService
      .getWorkshopBySlug(slug, false)
      .then((data) => {
        setWorkshop(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load workshop for registration:", err);
        setError("Unable to load registration details.");
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    loadWorkshop();
  }, [loadWorkshop]);

  // Dynamic SEO
  useEffect(() => {
    if (!workshop) return;

    const fallbackTitle = defaultTitleRef.current;
    const fallbackDesc = defaultDescRef.current;

    document.title = `Register: ${workshop.title} | Shiveda IT Services`;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && workshop.short_description) {
      metaDesc.setAttribute(
        "content",
        `Apply now for ${workshop.title} at Shiveda IT Services.`
      );
    }

    return () => {
      document.title = fallbackTitle;
      if (metaDesc && fallbackDesc) {
        metaDesc.setAttribute("content", fallbackDesc);
      }
    };
  }, [workshop]);

  const handleRegistrationComplete = (studentName) => {
    setRegisteredStudentName(studentName);
    setRegistrationSuccess(true);
    if (workshop?.id) {
      try {
        localStorage.setItem(`workshop_registered_${workshop.id}`, "true");
      } catch {
        // Ignore localStorage errors
      }
    }
    // Reload workshop in background to reflect decremented spots
    workshopsService.getWorkshopBySlug(slug, false).then((updated) => {
      if (updated) setWorkshop(updated);
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ============================================================
  // A. LOADING SKELETON
  // ============================================================
  if (loading) {
    return (
      <div className="w-full px-5 py-10 xl:px-10 2xl:px-14 max-w-7xl mx-auto space-y-8 animate-pulse">
        <div className="h-4 w-48 rounded bg-white/10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4">
          <div className="lg:col-span-5 space-y-6">
            <div className="h-80 rounded-3xl bg-white/5 border border-white/10" />
          </div>
          <div className="lg:col-span-7 space-y-6">
            <div className="h-96 rounded-3xl bg-white/5 border border-white/10" />
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // B. ERROR STATE
  // ============================================================
  if (error) {
    return (
      <div className="w-full max-w-lg mx-auto px-5 py-24 text-center">
        <div className="rounded-3xl border border-destructive/20 bg-destructive/5 p-8 sm:p-10 backdrop-blur-xl shadow-xl space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-destructive/10 border border-destructive/20 text-destructive mx-auto flex items-center justify-center">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-white">Registration Unavailable</h2>
          <p className="text-xs sm:text-sm text-white/65 leading-relaxed">{error}</p>
          <div className="pt-2">
            <Button
              onClick={loadWorkshop}
              className="rounded-xl bg-primary text-black hover:bg-primary/90 font-semibold gap-2"
            >
              <span>Try Again</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // C. 404 NOT FOUND STATE (Draft or non-existent)
  // ============================================================
  if (!workshop) {
    return (
      <div className="w-full max-w-xl mx-auto px-5 py-24 text-center">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-12 backdrop-blur-xl shadow-2xl space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto flex items-center justify-center">
            <Search className="w-7 h-7" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            Workshop Not Found
          </h2>
          <p className="text-sm text-white/60 leading-relaxed max-w-md mx-auto">
            The workshop you are looking to register for is unavailable, unpublished,
            or may have been removed from our public catalog.
          </p>
          <div className="pt-3">
            <Button
              onClick={() => navigate("/workshops")}
              className="rounded-xl bg-primary text-black hover:bg-primary/90 font-semibold px-6 shadow-[0_0_20px_rgba(0,194,255,0.4)]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>Browse All Workshops</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Capacity & derived values
  const capacity = Number(workshop.capacity) || 0;
  const registrationCount = Number(workshop.registration_count) || 0;
  const spotsRemaining =
    typeof workshop.spots_remaining === "number"
      ? workshop.spots_remaining
      : Math.max(0, capacity - registrationCount);
  const percentFilled =
    capacity > 0 ? Math.min(100, Math.round((registrationCount / capacity) * 100)) : 0;

  // ============================================================
  // D. COMPLETED / CANCELLED / CLOSED NON-REGISTERABLE STATES
  // ============================================================
  if (
    !registrationSuccess &&
    (workshop.status === "Completed" ||
      workshop.status === "Cancelled" ||
      !workshop.is_registration_open)
  ) {
    const isCompleted = workshop.status === "Completed";
    const isCancelled = workshop.status === "Cancelled";

    const titleText = isCancelled
      ? "Workshop Cancelled"
      : isCompleted
      ? "Workshop Completed"
      : "Registration Closed";

    const descText = isCancelled
      ? "This technical session has been cancelled and is not accepting student registrations."
      : isCompleted
      ? "This cohort session has concluded. Check back for upcoming dates or explore our other open workshops."
      : spotsRemaining <= 0
      ? "All available cohort seats have been filled for this session."
      : "Registration for this workshop is no longer accepting new applicants.";

    return (
      <div className="w-full max-w-xl mx-auto px-5 py-24 text-center">
        <div className="rounded-3xl border border-white/15 bg-white/[0.04] p-8 sm:p-12 backdrop-blur-2xl shadow-2xl space-y-5">
          <div
            className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${
              isCancelled
                ? "bg-destructive/15 border border-destructive/30 text-destructive"
                : "bg-white/10 border border-white/20 text-white/70"
            }`}
          >
            {isCancelled ? (
              <AlertCircle className="w-7 h-7" />
            ) : (
              <Lock className="w-7 h-7 text-primary" />
            )}
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            {titleText}
          </h2>

          <p className="text-sm text-white/65 leading-relaxed max-w-md mx-auto">
            {descText}
          </p>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to={`/workshops/${workshop.slug}`}>
              <Button
                variant="outline"
                className="w-full sm:w-auto rounded-xl border-white/15 bg-white/[0.04] text-xs font-semibold text-white hover:bg-white/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                <span>View Workshop Details</span>
              </Button>
            </Link>
            <Link to="/workshops">
              <Button className="w-full sm:w-auto rounded-xl bg-primary text-black hover:bg-primary/90 font-bold text-xs shadow-[0_0_20px_rgba(0,194,255,0.4)]">
                <span>Browse Active Workshops</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // E. SUCCESS CONFIRMATION VIEW
  // ============================================================
  if (registrationSuccess) {
    return (
      <div className="w-full px-5 py-10 xl:px-10 max-w-4xl mx-auto">
        <WorkshopRegistrationSuccess
          workshop={workshop}
          studentName={registeredStudentName}
        />
      </div>
    );
  }

  // ============================================================
  // F. ACTIVE REGISTRATION PAGE
  // ============================================================
  return (
    <div className="w-full px-5 py-8 sm:py-10 xl:px-10 2xl:px-14 max-w-7xl mx-auto space-y-8">
      {/* 1. Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center text-xs text-white/50">
        <ol className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <li>
            <Link to="/" className="hover:text-primary transition-colors">
              Home
            </Link>
          </li>
          <li aria-hidden="true" className="text-white/30">
            <ChevronRight className="w-3.5 h-3.5" />
          </li>
          <li>
            <Link to="/workshops" className="hover:text-primary transition-colors">
              Workshops
            </Link>
          </li>
          <li aria-hidden="true" className="text-white/30">
            <ChevronRight className="w-3.5 h-3.5" />
          </li>
          <li>
            <Link
              to={`/workshops/${workshop.slug}`}
              className="hover:text-primary transition-colors truncate max-w-[180px] sm:max-w-xs"
            >
              {workshop.title}
            </Link>
          </li>
          <li aria-hidden="true" className="text-white/30">
            <ChevronRight className="w-3.5 h-3.5" />
          </li>
          <li aria-current="page" className="text-white font-medium">
            Register
          </li>
        </ol>
      </nav>

      {/* 2. Page Header Banner */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/15 text-primary border border-primary/30">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Student Registration</span>
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/90 border border-white/15">
            {workshop.category}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
          Apply for {workshop.title}
        </h1>
        <p className="text-sm text-white/65 max-w-3xl leading-relaxed">
          Complete the 5 application fields below. Selected students will receive an
          interactive lab invite and curriculum access package.
        </p>
      </div>

      {/* 3. Two-Column Layout (Summary Card on left, Form on right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        {/* Left Column: Workshop Summary Card (40%) */}
        <div className="lg:col-span-5 space-y-6">
          <Reveal delay={40}>
            <div className="rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.45)] space-y-6">
              {/* Cover preview */}
              <div className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden bg-black/60 border border-white/10">
                <img
                  src={workshop.cover_image}
                  alt={workshop.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
                <div className="absolute bottom-3 left-3.5 right-3.5 flex items-center justify-between text-xs">
                  <span className="px-2.5 py-0.5 rounded-full bg-black/70 border border-white/20 text-white font-medium">
                    {workshop.mode}
                  </span>
                  {workshop.is_free ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-green-500/20 border border-green-500/30 font-bold text-xs">
                      <span className="text-[10px] text-white/50 line-through font-normal">₹999</span>
                      <span className="text-green-400">FREE</span>
                    </div>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold">
                      ₹{workshop.price?.toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </div>

              {/* Title & Short description */}
              <div className="space-y-2">
                <h2 className="text-lg font-bold text-white tracking-tight">
                  {workshop.title}
                </h2>
                <p className="text-xs text-white/65 leading-relaxed">
                  {workshop.short_description}
                </p>
              </div>

              {/* Seat Capacity Bar */}
              <div className="space-y-2 p-3.5 rounded-2xl border border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/60 flex items-center gap-1.5 font-medium">
                    <Users className="w-3.5 h-3.5 text-primary" />
                    <span>Cohort Seats</span>
                  </span>
                  <span className="text-white font-semibold">
                    {spotsRemaining} {spotsRemaining === 1 ? "seat" : "seats"} left
                  </span>
                </div>
                <Progress value={percentFilled} className="h-1.5 bg-white/10" />
                <div className="text-[11px] text-white/45 flex justify-between">
                  <span>{registrationCount} enrolled</span>
                  <span>Max {capacity} students</span>
                </div>
              </div>

              {/* Key Logistics Strip */}
              <div className="space-y-2.5 text-xs text-white/70 border-t border-white/10 pt-4">
                <div className="flex items-center justify-between py-1 border-b border-white/5">
                  <span className="flex items-center gap-2 text-white/50">
                    <Calendar className="w-3.5 h-3.5 text-primary" />
                    <span>Date</span>
                  </span>
                  <span className="font-semibold text-white">{workshop.date}</span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-white/5">
                  <span className="flex items-center gap-2 text-white/50">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span>Time</span>
                  </span>
                  <span className="font-semibold text-white">
                    {workshop.start_time} - {workshop.end_time}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-white/5">
                  <span className="flex items-center gap-2 text-white/50">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span>Duration</span>
                  </span>
                  <span className="font-semibold text-white">{workshop.duration}</span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-white/5">
                  <span className="flex items-center gap-2 text-white/50">
                    <MapPin className="w-3.5 h-3.5 text-primary" />
                    <span>Delivery</span>
                  </span>
                  <span className="font-semibold text-white truncate max-w-[170px]" title={workshop.venue || workshop.mode}>
                    {workshop.mode === "Online" ? "Virtual Live Stream" : workshop.venue || workshop.mode}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className="flex items-center gap-2 text-white/50">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                    <span>Certificate</span>
                  </span>
                  <span className="font-semibold text-white">
                    {workshop.certificate_available ? "Verified Credential" : "N/A"}
                  </span>
                </div>
              </div>

              {/* Link back to detail */}
              <div className="pt-2">
                <Link
                  to={`/workshops/${workshop.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Review full curriculum & agenda</span>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right Column: 5-Field Registration Form (60%) */}
        <div className="lg:col-span-7">
          <Reveal delay={80}>
            <WorkshopRegistrationForm
              workshop={workshop}
              onSuccess={handleRegistrationComplete}
            />
          </Reveal>
        </div>
      </div>
    </div>
  );
}
